import { useEffect, useState } from 'react';
import { portalApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Clock, MapPin, CheckCircle2, XCircle, CalendarDays, Repeat, Trash2, Plus } from 'lucide-react';
import { fmtEdTime, fmtEdDate, edDayStart, edAddDays, edParts } from '@/lib/tz';

function startOfWeek(date) {
  const start = edDayStart(date);
  return edAddDays(start, -((edParts(start).weekday + 6) % 7));
}
const fmtTime = (d) => fmtEdTime(d, '12h');
const fmtDate = (d) => fmtEdDate(d, { weekday: 'short', month: 'short', day: 'numeric' });
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TO_TYPES = ['VACATION', 'UNPAID', 'PERSONAL', 'SICK', 'OTHER'];

export default function PortalSchedule() {
  return (
    <div className="space-y-4" data-testid="portal-schedule">
      <div>
        <h1 className="font-heading text-2xl font-700 text-charcoal">My Schedule</h1>
        <p className="text-sm text-muted-ink">Your shifts, availability, time off and open shifts.</p>
        <p className="text-[11px] text-muted-ink mt-0.5" data-testid="portal-schedule-tz">All times shown in Mountain Time (Edmonton)</p>
      </div>
      <Tabs defaultValue="shifts">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="shifts" data-testid="ps-tab-shifts">Shifts</TabsTrigger>
          <TabsTrigger value="availability" data-testid="ps-tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="timeoff" data-testid="ps-tab-timeoff">Time Off</TabsTrigger>
          <TabsTrigger value="open" data-testid="ps-tab-open">Open</TabsTrigger>
        </TabsList>
        <TabsContent value="shifts" className="mt-4"><MyShifts /></TabsContent>
        <TabsContent value="availability" className="mt-4"><MyAvailability /></TabsContent>
        <TabsContent value="timeoff" className="mt-4"><MyTimeOff /></TabsContent>
        <TabsContent value="open" className="mt-4"><OpenShifts /></TabsContent>
      </Tabs>
    </div>
  );
}

function MyShifts() {
  const [shifts, setShifts] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    const ws = startOfWeek(new Date());
    portalApi.get('/scheduling/me/shifts', { params: { from: ws.toISOString(), to: new Date(ws.getTime() + 21 * 86400000).toISOString() } }).then(({ data }) => setShifts(data.shifts));
    portalApi.get('/scheduling/me/swaps').then(({ data }) => setIncoming(data.incoming)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    setBusyId(id);
    try { await portalApi.post(`/scheduling/me/shifts/${id}/respond`, { action }); toast.success(action === 'CONFIRM' ? 'Confirmed' : 'Declined'); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusyId(null); }
  };
  const requestCover = async (id) => {
    try { await portalApi.post(`/scheduling/me/shifts/${id}/swap`, { type: 'REPLACEMENT' }); toast.success('Cover requested — your manager will review'); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const acceptSwap = async (id) => {
    try { const { data } = await portalApi.post(`/scheduling/me/swaps/${id}/accept`); toast.success(data.applied ? 'Shift is now yours' : 'Accepted — pending manager approval'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const now = new Date();
  const upcoming = shifts.filter((s) => new Date(s.endAt) >= now);

  return (
    <div className="space-y-3">
      {incoming.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="incoming-swaps">
          <p className="text-sm font-medium text-amber-800">Cover / swap requests for you</p>
          {incoming.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-sm">
              <span>{r.shift ? `${fmtDate(r.shift.startAt)} ${fmtTime(r.shift.startAt)}–${fmtTime(r.shift.endAt)}` : 'Shift'}</span>
              <Button size="sm" data-testid="accept-swap-btn" onClick={() => acceptSwap(r.id)} className="bg-coriander hover:bg-coriander/90">Accept</Button>
            </div>
          ))}
        </div>
      )}
      {upcoming.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border/40 p-6 text-center text-sm text-muted-ink flex flex-col items-center gap-2" data-testid="portal-schedule-empty">
          <CalendarDays size={28} className="text-muted-ink/50" /> No upcoming published shifts yet.
        </div>
      ) : upcoming.map((s) => (
        <div key={s.id} className="bg-white rounded-2xl border border-border/40 p-4" data-testid="my-shift-card">
          <div className="flex items-center justify-between">
            <p className="font-heading font-700 text-charcoal">{fmtDate(s.startAt)}</p>
            {s.position && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: (s.position.color || '#E8833A') + '22', color: s.position.color || '#E8833A' }}>{s.position.name}</span>}
          </div>
          <div className="flex items-center gap-2 text-sm text-charcoal mt-1.5"><Clock size={14} className="text-muted-ink" /> {fmtTime(s.startAt)} – {fmtTime(s.endAt)} · {s.hours.paid}h paid</div>
          {s.location && <div className="flex items-center gap-2 text-sm text-muted-ink mt-1"><MapPin size={14} /> {s.location}</div>}
          {s.updatedAfterPublish && <p className="text-xs text-blue-600 font-semibold mt-1">Updated by manager</p>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {s.status === 'CONFIRMED' ? (
              <span className="text-sm text-coriander flex items-center gap-1.5" data-testid="my-shift-confirmed"><CheckCircle2 size={16} /> Confirmed</span>
            ) : s.status === 'DECLINED' ? (
              <span className="text-sm text-red-600 flex items-center gap-1.5"><XCircle size={16} /> Declined</span>
            ) : (
              <>
                <Button size="sm" data-testid="my-shift-confirm-btn" disabled={busyId === s.id} onClick={() => respond(s.id, 'CONFIRM')} className="bg-coriander hover:bg-coriander/90 gap-1.5"><CheckCircle2 size={14} /> Confirm</Button>
                <Button size="sm" variant="outline" data-testid="my-shift-decline-btn" disabled={busyId === s.id} onClick={() => respond(s.id, 'DECLINE')} className="text-destructive gap-1.5"><XCircle size={14} /> Decline</Button>
              </>
            )}
            <Button size="sm" variant="ghost" data-testid="my-shift-cover-btn" onClick={() => requestCover(s.id)} className="gap-1.5 text-muted-ink"><Repeat size={14} /> Request cover</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyAvailability() {
  const [items, setItems] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  const [day, setDay] = useState('1');
  const [type, setType] = useState('UNAVAILABLE');

  const load = () => portalApi.get('/scheduling/me/availability').then(({ data }) => { setItems(data.availability); setCanEdit(data.canEdit); });
  useEffect(() => { load(); }, []);

  const add = async () => {
    try { await portalApi.post('/scheduling/me/availability', { dayOfWeek: Number(day), type }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => { try { await portalApi.delete(`/scheduling/me/availability/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); } };

  return (
    <div className="space-y-3" data-testid="portal-availability">
      {!canEdit && <p className="text-xs text-amber-600">Availability changes are currently disabled by your manager.</p>}
      <div className="bg-white rounded-2xl border border-border/40 divide-y divide-border/30">
        {items.map((a) => (
          <div key={a.id} className="p-3 flex items-center justify-between" data-testid="availability-row">
            <span className={`text-sm ${a.type === 'UNAVAILABLE' ? 'text-red-600' : a.type === 'PREFERRED' ? 'text-saffron' : 'text-charcoal'}`}>{DAYS[a.dayOfWeek]} · {a.type}</span>
            {canEdit && <button data-testid="availability-delete-btn" onClick={() => del(a.id)} className="text-destructive"><Trash2 size={15} /></button>}
          </div>
        ))}
        {items.length === 0 && <p className="p-4 text-sm text-muted-ink">No availability set yet.</p>}
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <Select value={day} onValueChange={setDay}>
            <SelectTrigger data-testid="availability-day-select" className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="availability-type-select" className="flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="AVAILABLE">Available</SelectItem>
              <SelectItem value="PREFERRED">Preferred</SelectItem>
              <SelectItem value="UNAVAILABLE">Unavailable</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" data-testid="availability-add-btn" onClick={add} className="bg-saffron hover:bg-saffron-hover gap-1"><Plus size={14} /> Add</Button>
        </div>
      )}
    </div>
  );
}

function MyTimeOff() {
  const [requests, setRequests] = useState([]);
  const [canRequest, setCanRequest] = useState(true);
  const [form, setForm] = useState({ type: 'VACATION', startDate: '', endDate: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const load = () => portalApi.get('/scheduling/me/time-off').then(({ data }) => { setRequests(data.requests); setCanRequest(data.canRequest); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.startDate || !form.endDate) return toast.error('Pick start and end dates');
    setBusy(true);
    try { await portalApi.post('/scheduling/me/time-off', form); toast.success('Request submitted'); setForm({ type: 'VACATION', startDate: '', endDate: '', reason: '' }); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const cancel = async (id) => { try { await portalApi.post(`/scheduling/me/time-off/${id}/cancel`); load(); } catch (err) { toast.error(formatApiError(err)); } };

  const badge = (s) => ({ PENDING: 'bg-amber-100 text-amber-700', APPROVED: 'bg-coriander/10 text-coriander', REJECTED: 'bg-red-100 text-red-700', CANCELLED: 'bg-slate-100 text-slate-500' }[s]);

  return (
    <div className="space-y-3" data-testid="portal-timeoff">
      {canRequest && (
        <div className="bg-white rounded-2xl border border-border/40 p-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="timeoff-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Reason</Label><Input data-testid="timeoff-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
            <div className="space-y-1"><Label>From</Label><Input type="date" data-testid="timeoff-start-input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></div>
            <div className="space-y-1"><Label>To</Label><Input type="date" data-testid="timeoff-end-input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></div>
          </div>
          <Button size="sm" data-testid="timeoff-submit-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover w-full">{busy ? 'Submitting…' : 'Request Time Off'}</Button>
        </div>
      )}
      <div className="space-y-2">
        {requests.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-border/40 p-3 flex items-center justify-between" data-testid="timeoff-row">
            <div>
              <p className="text-sm font-medium text-charcoal">{r.type} · <span className={`text-xs px-2 py-0.5 rounded-full ${badge(r.status)}`}>{r.status}</span></p>
              <p className="text-xs text-muted-ink">{new Date(r.startDate).toLocaleDateString('en-CA')} – {new Date(r.endDate).toLocaleDateString('en-CA')}</p>
            </div>
            {r.status === 'PENDING' && <button data-testid="timeoff-cancel-btn" onClick={() => cancel(r.id)} className="text-xs text-destructive">Cancel</button>}
          </div>
        ))}
        {requests.length === 0 && <p className="text-sm text-muted-ink">No time-off requests yet.</p>}
      </div>
    </div>
  );
}

function OpenShifts() {
  const [shifts, setShifts] = useState([]);
  const [canClaim, setCanClaim] = useState(true);

  const load = () => portalApi.get('/scheduling/me/open-shifts').then(({ data }) => { setShifts(data.shifts); setCanClaim(data.canClaim); });
  useEffect(() => { load(); }, []);

  const claim = async (id) => {
    try { const { data } = await portalApi.post(`/scheduling/me/open-shifts/${id}/claim`); toast.success(data.autoApproved ? 'Shift is yours!' : 'Claim sent for approval'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-3" data-testid="portal-open-shifts">
      {!canClaim && <p className="text-xs text-amber-600">Claiming open shifts is currently disabled.</p>}
      {shifts.length === 0 ? (
        <p className="text-sm text-muted-ink">No open shifts you can claim right now.</p>
      ) : shifts.map((s) => (
        <div key={s.id} className="bg-white rounded-2xl border border-border/40 p-4 flex items-center justify-between" data-testid="open-shift-row">
          <div>
            <p className="font-heading font-700 text-charcoal">{fmtDate(s.startAt)}</p>
            <p className="text-sm text-muted-ink">{fmtTime(s.startAt)} – {fmtTime(s.endAt)} · {s.hours.paid}h{s.position ? ` · ${s.position.name}` : ''}</p>
          </div>
          {s.alreadyClaimed ? (
            <span className="text-xs text-amber-600">Claim pending</span>
          ) : (
            <Button size="sm" data-testid="claim-open-shift-btn" onClick={() => claim(s.id)} className="bg-saffron hover:bg-saffron-hover">Claim</Button>
          )}
        </div>
      ))}
    </div>
  );
}
